import { Guid } from "guid-typescript";
import _ from "lodash";
import { CSSProperties, PureComponent, ReactNode } from "react";
import ReactDOM from "react-dom";
import isEqual from "react-fast-compare";
import withCommonEvents from "../../../shared/hoc/with-common-events";
import DynamicStyleManager from "../../../shared/utilty/dynamic-style-manager";
import { KuikaAppManager } from "../../../shared/utilty/kuika-app-manager";
import { KMainFunctions } from "../../../shared/utilty/main-functions";
import { CommonProps } from "../common/common-props";

interface PanelProps {
  children?: ReactNode;
  style?: CSSProperties;
  scrolling?: "horizontal" | "vertical" | "auto" | "hidden";
  scrollBackColor?: string;
  scrollWidth?: number;
  scrollRadius: boolean;
  dynamicBackGroundColor: string;
  dynamicBgImage: string;
  dynamicFontColor: string;
  hoverBackgroundColor?: string;
  hoverFontColor?: string;
  isVertical?: boolean;
}

interface PanelState {}

class Panel extends PureComponent<PanelProps & CommonProps, PanelState> {
  private uniqueKey = Guid.create();

  private style = new DynamicStyleManager(`kpanel_${this.uniqueKey}`);

  constructor(props: PanelProps) {
    super(props);
    this.state = {};
  }

  componentDidMount = () => {
    const uniqueKey: string = this.uniqueKey.toString();

    if (!uniqueKey) {
      return;
    }

    this.updateStyle();
    this.setDynamicStyle();
  };

  componentDidUpdate(prevProps: PanelProps): void {
    this.updateStyle();

    if (!isEqual(prevProps, this.props)) {
      this.setDynamicStyle();
    }
  }

  componentWillUnmount() {
    this.style.removeStyle();
  }

  setDynamicStyle = () => {
    this.style.setDynamicCss(this.getDynamicCss());
  };

  updateStyle = () => {
    const node: any = ReactDOM.findDOMNode(this);
    let style: React.CSSProperties = _.clone(this.props.style);
    if (this.props?.style?.alignItems !== "flex-start" && node && node.children && node.children.length > 0) {
      KMainFunctions.handleAlignments(node, style);
    }
  };

  addHashIfNeeded = (value: string): string => {
    if (value && value.length === 6 && !value.startsWith("#")) {
      return `#${value}`;
    }
    return value;
  };

  getStyle = (): any => {
    let style: CSSProperties = {};

    if (this.props.style) {
      style = { ...this.props.style };
    }

    if (this.props.scrolling) {
      if (this.props.scrolling === "horizontal") {
        style.overflowX = "auto";
        style.overflowY = "hidden";
      }
      if (this.props.scrolling === "vertical") {
        style.overflowX = "hidden";
        style.overflowY = "auto";
      }
      if (this.props.scrolling === "auto") {
        style.overflow = "auto";
      }
      if (this.props.scrolling === "hidden" && (this.props.style?.height || this.props.style?.maxHeight)) {
        style.overflow = "hidden";
      }
    }
    // test2
    if (this.props.dynamicBackGroundColor && this.props.dynamicBackGroundColor.length > 0) {
      style.backgroundColor = this.addHashIfNeeded(this.props.dynamicBackGroundColor);
    }

    if (this.props.dynamicBgImage && this.props.dynamicBgImage.length > 0) {
      let bgImage = this.props.dynamicBgImage;
      if (Guid.isGuid(bgImage)) {
        bgImage = `url(${KuikaAppManager.GetBackendUrl()}/resource/runtime/byid?resourceId=${bgImage})`;
      } else if (bgImage.startsWith("/image")) {
        bgImage = `url(${KuikaAppManager.GetBackendUrl()}${bgImage})`;
      } else {
        bgImage = `url(${bgImage})`;
      }
      style.backgroundSize = "cover";
      style.backgroundImage = bgImage;
    }

    if (this.props.dynamicFontColor && this.props.dynamicFontColor.length > 0) {
      style.color = this.addHashIfNeeded(this.props.dynamicFontColor);
    }

    if (this.props.isVertical) {
      style.height = "100%";
    }

    return style;
  };

  getDynamicCss = (): string => {
    const className: string = this.getClassName();

    if (!className || className.length === 0) {
      return "";
    }

    const scrollBackColor = this.getScrollBackColor();
    const scrollRadius = this.getScrollRadius();
    const scrollWidth = this.getScrollWidth();
    let result = "";

    if (scrollWidth) {
      result = `
        .${className}::-webkit-scrollbar {
          ${scrollWidth}
        }`;
    }

    if (scrollBackColor || scrollRadius) {
      result = `${result}.${className}::-webkit-scrollbar-thumb {
          ${scrollBackColor ?? ``}
          ${scrollRadius ?? ``}
        }`;

      result = `${result}.${className}::-webkit-scrollbar-thumb:hover {
          ${scrollBackColor ?? ``}
          ${scrollRadius ?? ``}
        }`;
    }

    if (this.props.dynamicFontColor) {
      result = `${result}.${className} * {
        color: ${this.addHashIfNeeded(this.props.dynamicFontColor)} !important;
      }`;
    }

    if (this.props.hoverBackgroundColor) {
      result = `${result}.${className}:hover{ 
        background-color: ${this.addHashIfNeeded(this.props.hoverBackgroundColor)} 
      }`;
    }

    if (this.props.hoverFontColor) {
      result = `${result}.${className}:hover{ 
        color: ${this.addHashIfNeeded(this.props.hoverFontColor)} 
      }`;
    }

    return result;
  };

  getScrollBackColor = (): string | undefined => {
    if (this.props.scrollBackColor && this.props.scrollBackColor.length > 0) {
      return `background: ${this.props.scrollBackColor};`;
    }
  };

  getScrollRadius = (): string | undefined => {
    if (this.props.scrollRadius && this.props.scrollRadius === true) {
      return "border-radius: 20px;";
    }
  };

  getScrollWidth = (): string | undefined => {
    if (this.props.scrollWidth && this.props.scrollWidth > 0) {
      return `width: ${this.props.scrollWidth}px;
      height: ${this.props.scrollWidth}px;`;
    }
  };

  getClassName = (): string => {
    let result = "";

    if (this.props.className) {
      result = `${this.props.className} `;
    }

    if (!this.uniqueKey) {
      return result;
    }

    result = `${result}panel_${this.uniqueKey.toString().substring(0, 8)}`;
    return result;
  };

  getProps = () => {
    const props: any = _.clone(this.props);

    if (props.children) {
      delete props.children;
    }

    return props;
  };

  render(): ReactNode {
    return (
      <div {...this.getProps()} onClick={this.props?.onClick} className={this.getClassName()} style={this.getStyle()}>
        {this.props.children}
      </div>
    );
  }
}

const panel = withCommonEvents(Panel);
export { panel as Panel };
